/**
 * OfflineQueue - localStorage buffer for failed database operations
 *
 * Stores operations when database is unavailable and provides FIFO queue
 * management with max size limit to prevent unbounded growth.
 */

export interface QueuedOperation {
  id: string;
  timestamp: number;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data: any;
  retryCount: number;
  maxRetries: number;
}

export class OfflineQueue {
  private static QUEUE_KEY = 'af_queue_operations';
  private static MAX_QUEUE_SIZE = 1000;
  private static MAX_BYTES = 4_000_000; // 4MB safety margin (browser limit ~5-10MB)
  private static MAX_OPERATION_BYTES = 100_000; // 100KB per operation — skip large blobs like base64 media

  /**
   * Add operation to queue
   * Automatically assigns ID, timestamp, and initializes retry count.
   * Skips operations that are too large (e.g. base64 media uploads).
   */
  static enqueue(
    operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retryCount' | 'maxRetries'>
  ): void {
    const newOp: QueuedOperation = {
      ...operation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: 3,
    };

    // Reject oversized operations (e.g. base64-encoded media)
    const opSize = new Blob([JSON.stringify(newOp)]).size;
    if (opSize > this.MAX_OPERATION_BYTES) {
      console.warn(
        `⚠️ Operation too large (${(opSize / 1024).toFixed(0)}KB), skipping offline queue for: ${newOp.endpoint}`
      );
      return;
    }

    const queue = this.getQueue();
    queue.push(newOp);

    // FIFO: remove oldest if queue full
    if (queue.length > this.MAX_QUEUE_SIZE) {
      console.warn(`⚠️ Queue full (${this.MAX_QUEUE_SIZE}), removing oldest operation`);
      queue.shift();
    }

    // Remove expired operations (older than 24 hours)
    const MAX_AGE_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const freshQueue = queue.filter((op) => now - op.timestamp < MAX_AGE_MS);
    if (freshQueue.length < queue.length) {
      console.warn(`⚠️ Removed ${queue.length - freshQueue.length} expired operations (>24h)`);
    }

    this.safeSetQueue(freshQueue);
  }

  /**
   * Get all queued operations
   */
  static getQueue(): QueuedOperation[] {
    try {
      const data = localStorage.getItem(this.QUEUE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to parse queue from localStorage:', error);
      return [];
    }
  }

  /**
   * Remove operation from queue by ID
   */
  static dequeue(id: string): void {
    const queue = this.getQueue().filter((op) => op.id !== id);
    this.safeSetQueue(queue);
  }

  /**
   * Update retry count for an operation
   */
  static incrementRetry(id: string): void {
    const queue = this.getQueue();
    const op = queue.find((op) => op.id === id);
    if (op) {
      op.retryCount++;
      this.safeSetQueue(queue);
    }
  }

  /**
   * Clear all queued operations
   */
  static clear(): void {
    localStorage.removeItem(this.QUEUE_KEY);
  }

  /**
   * Get current queue size
   */
  static size(): number {
    return this.getQueue().length;
  }

  /**
   * Get operations by endpoint (useful for debugging)
   */
  static getByEndpoint(endpoint: string): QueuedOperation[] {
    return this.getQueue().filter((op) => op.endpoint === endpoint);
  }

  /**
   * Get failed operations (maxRetries reached)
   */
  static getFailedOperations(): QueuedOperation[] {
    return this.getQueue().filter((op) => op.retryCount >= op.maxRetries);
  }

  /**
   * Safe localStorage write with byte-size check and QuotaExceededError handling
   */
  private static safeSetQueue(queue: QueuedOperation[]): void {
    const serialized = JSON.stringify(queue);
    const bytes = new Blob([serialized]).size;

    // Trim oldest operations if approaching storage limit
    while (bytes > this.MAX_BYTES && queue.length > 0) {
      queue.shift();
    }

    try {
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.error('⚠️ localStorage quota exceeded — clearing oldest 50 operations');
        queue.splice(0, Math.min(50, queue.length));
        try {
          localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
        } catch {
          console.error('⚠️ localStorage still full — clearing entire queue');
          localStorage.removeItem(this.QUEUE_KEY);
        }
      }
    }
  }
}
